import React, { ChangeEvent, useEffect, useRef } from "react";
import Link from "../../../../components/elements/link";
import InputField from "../../../../components/form-elements/input-field";
import Select from "../../../../components/form-elements/select";
import { ROUTES } from "../../../../routes";
import { sort } from "../../../../helpers/array";
import { topLevelCategoryNames } from "../../../../helpers/category";
import { stringToSlug } from "../../../../helpers/string";
import useForm from "../../../../hooks/useForm";
import useFormRefs from "../../../../hooks/useFormRefs";
import useQuery from "../../../../hooks/useQuery";
import useRouteParams from "../../../../hooks/useRouteParams";
import type { Category } from "../../../../interfaces";
//import { useCategories } from "../../../../store/categories";
import Minicart from "./helper-components/mini-cart";
import WishlistCount from "./helper-components/wishlist-count";

export default function HeaderMiddleArea(): React.JSX.Element {
  const [queryStringData] = useQuery();
  const { categoryName } = useRouteParams(); // Only applies if we are on the CATEGORY (/shop/categories/:categoryName) page
  //const [categoriesData, fetchCategoriesData] = useCategories();
  const { values: formData, handleChange: handleFormFieldChange } = useForm(
    { 
      query: queryStringData.query ?? "", 
      category: queryStringData.category?.toLowerCase() ?? categoryName?.toLowerCase() ?? "" 
    }, 
    { 
      onInputChange: (e: ChangeEvent<HTMLInputElement|HTMLSelectElement|HTMLTextAreaElement>) => {
      if(e.target.name === "category") {
        refs?.query?.current?.focus(); // We are able to get access to refs due to closure
      }
    }}
  );

  const [refs] = useFormRefs(formData);
  const searchBtnRef = useRef<HTMLButtonElement | null>(null);
  const topLevelCategories = topLevelCategoryNames
    .filter(name => name.toLowerCase() !== "other categories")
    .map(name => ({ id: stringToSlug(name), name: name }));
  
  const categoriesData = { categories: topLevelCategories };
  const sortedCategories = (
    sort<Category>((categoriesData?.categories ?? []), { by: "name" })
    .concat(topLevelCategoryNames
      .filter(name => name.toLowerCase() === "other categories")
      .map(name => ({ id: stringToSlug(name), name: name }))
    )
  );

  //useEffect(() => { fetchCategoriesData(); }, []);

  useEffect(() => {
    if(refs.category?.current) {
      refs.category.current.addEventListener("focus", handleCategoryFieldFocus);
      refs.category.current.addEventListener("blur", handleCategoryFieldBlur);

      refs.category.current.addEventListener("mouseover", handleFormFieldMouseOver);
      refs.category.current.addEventListener("mouseout", handleFormFieldMouseOut);
    }

    if(refs.query?.current) {
      refs.query.current.addEventListener("focus", handleQueryFieldFocus);
      refs.query.current.addEventListener("blur", handleQueryFieldBlur);

      refs.query.current.addEventListener("mouseover", handleFormFieldMouseOver);
      refs.query.current.addEventListener("mouseout", handleFormFieldMouseOut);
    }

    return function cleanup() {
      if(refs.category?.current) {
        refs.category.current.removeEventListener("focus", handleCategoryFieldFocus);
        refs.category.current.removeEventListener("blur", handleCategoryFieldBlur);

        refs.category.current.removeEventListener("mouseover", handleFormFieldMouseOver);
        refs.category.current.removeEventListener("mouseout", handleFormFieldMouseOut);
      }

      if(refs?.query?.current) {
        refs.query.current.removeEventListener("focus", handleQueryFieldFocus);
        refs.query.current.removeEventListener("blur", handleQueryFieldBlur);

        refs.query.current.removeEventListener("mouseout", handleFormFieldMouseOver);
        refs.query.current.removeEventListener("mouseout", handleFormFieldMouseOut);
      }
    }

    function handleCategoryFieldFocus() {
      if(refs.query?.current) {
        refs.query.current.classList.add("bordered");
      }

      if(searchBtnRef?.current) {
        searchBtnRef.current.classList.add("bordered");
      }

      // If we hovered over the category or search input element before focusing, 
      // undo the hover effect previously applied.
      handleFormFieldMouseOut();
    }

    function handleCategoryFieldBlur() {
      if(refs.query?.current) {
        refs.query.current.classList.remove("bordered");
      }

      if(searchBtnRef?.current) {
        searchBtnRef.current.classList.remove("bordered");
      }
    }

    function handleQueryFieldFocus() {
      if(refs.category?.current) {
        refs.category.current.classList.add("bordered");
      }

      if(searchBtnRef?.current) {
        searchBtnRef.current.classList.add("bordered");
      }

      // If we hovered over the category or search input element before focusing, 
      // undo the hover effect previously applied.
      handleFormFieldMouseOut();
    }

    function handleQueryFieldBlur() {
      if(refs.category?.current) {
        refs.category.current.classList.remove("bordered");
      }

      if(searchBtnRef?.current) {
        searchBtnRef.current.classList.remove("bordered");
      }
    }

    function handleFormFieldMouseOver() {
      if([refs.category?.current, refs.query?.current].includes(document.activeElement as (HTMLInputElement | HTMLSelectElement))) {
        // If the category field or the search input field has focus, 
        // then, ignore the hover (mouseover).
        return;
      }

      if(refs.category?.current) {
        refs.category.current.classList.add("hovered");
      }

      if(refs.query?.current) {
        refs.query.current.classList.add("hovered");
      }
    }

    function handleFormFieldMouseOut() {
      if(refs.category?.current) {
        refs.category.current.classList.remove("hovered");
      }

      if(refs.query?.current) {
        refs.query.current.classList.remove("hovered");
      }
    }
  }, [refs.category?.current, refs.query?.current]);

  return (
    <div className="header-middle pl-sm-0 pr-sm-0 pl-xs-0 pr-xs-0">
      <div className="container">
        <div className="row">
          {/* Begin Header Logo Area */}
          <div className="col-lg-3">
            <div className="brand logo pb-sm-30 pb-xs-30">
              <Link to={ROUTES.HOME} end>
                {/* <img src="/images/menu/logo/logo.png" alt={`${process.env.APP_NAME} logo`} /> */}
                <img className="brand-logo" src="/images/branding/SecondaryLogo.png" alt={`${process.env.APP_NAME} logo`} />
              </Link>
            </div>
          </div>
          {/* Header Logo Area End Here */}

          {/* Begin Header Middle Right Area */}
          <div className="col-lg-9 pl-0 ml-sm-15 ml-xs-15">
            {/* Begin Header Middle Searchbox Area */}
            <form action={ROUTES.SEARCH} className="hm-searchbox raised">
              <Select name="category"
                className="nice-select select-search-category"
                ref={refs.category as React.RefObject<HTMLSelectElement>}
                value={formData.category.toLowerCase()}
                onChange={handleFormFieldChange}
              >
                <option value="">All Categories</option>

                { sortedCategories.map(({ id, name }) => (
                  <option key={id} value={name.toLowerCase()}>{name}</option>
                ))}
              </Select>
              <InputField name="query" 
                placeholder="Enter your search term ..." 
                ref={refs.query as React.RefObject<HTMLInputElement>}
                value={formData.query}
                onChange={handleFormFieldChange} 
              />
              <button className="li-btn header-search-btn raised" type="submit" ref={searchBtnRef}>
                <i className="fa fa-search"></i>
              </button>
            </form>
            {/* Header Middle Searchbox Area End Here */}

            {/* Begin Header Middle Right Area */}
            <div className="header-middle-right">
              <ul className="hm-menu">
                {/* Begin Header Middle Wishlist Area */}
                <li className="hm-wishlist raised" title="My Wishlist">
                  <WishlistCount />
                </li>
                {/* Header Middle Wishlist Area End Here */}

                {/* Begin Header Mini Cart Area */}
                <li className="hm-minicart raised">
                  <Minicart />
                </li>
                {/* Header Mini Cart Area End Here */}
              </ul>
            </div>
            {/* Header Middle Right Area End Here */}
          </div>
          {/* Header Middle Right Area End Here */}
        </div>
      </div>
    </div>
  );
}
